/**
 * LegalPage — the shared frame for privacy, terms, refund, cancellation and
 * the service level agreement.
 *
 * These pages are structurally identical: a title, a last-reviewed date, and a
 * run of headed sections. One frame means a change to the reading measure or
 * the heading rhythm lands on all of them.
 *
 * ── The two-column layout ─────────────────────────────────────────────────
 * Prose stays at a reading measure, because 68 characters is where long text
 * is comfortable and the full 1152px of the shell is not. But a lone column of
 * that width inside the shell looked like a rendering fault next to every other
 * page, so the space it was leaving now holds the contents list on the right -
 * which a document of numbered obligations wanted anyway. Below lg the list
 * falls below the text, where it reads as a summary rather than navigation.
 *
 * `draft` still exists for a page whose sections are prompts. The ones shipped
 * here are not; each asserts something the platform actually does.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@bloomneo/uikit';
import { Info } from 'lucide-react';
import { SEO } from '@/shared/SEO';

export type LegalSection = { heading: string; body: ReactNode };

/** A heading as a URL fragment, so the contents list can point at it. */
function slug(heading: string): string {
  return heading.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export function LegalPage({
  title,
  description,
  updated,
  sections,
  /**
   * A lead paragraph, set above the sections and below the title.
   *
   * Optional because the four template pages are prompts and have nothing to
   * lead with. A page carrying real policy usually does — a plain statement of
   * what the document covers, before the headings start.
   */
  intro,
  /** Set false once you have replaced the placeholder copy. */
  draft = true,
}: {
  title: string;
  description: string;
  updated: string;
  sections: LegalSection[];
  intro?: ReactNode;
  draft?: boolean;
}) {
  return (
    <>
      <SEO title={title} description={description} />

      {/* ~68 characters per line. Long-form text set to the full page width is
          measurably harder to read; this is the one place it matters most. */}
      <div className="py-8 lg:grid lg:grid-cols-[minmax(0,1fr)_16rem] lg:gap-12">
        {/*
          Contents, on the right. Sticky on a wide screen so it stays with the
          reader; on a narrow one it falls below the text, where a list of
          headings someone has already read is a summary rather than dead
          weight above the thing they came for. Ordinary anchors - no
          scroll-spy, no JavaScript, and it works in the pre-rendered HTML.
        */}
      <article className="min-w-0 lg:max-w-[68ch]">
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last reviewed {updated}</p>

        {draft && (
          <Alert className="mt-6 border-warning/40 bg-warning/8">
            <Info className="size-4 text-warning" />
            <AlertDescription>
              This is placeholder text, not legal advice. Replace every section
              below with policy that reflects how you actually operate, and have
              it reviewed before you publish.
            </AlertDescription>
          </Alert>
        )}

        {intro && (
          <p className="mt-6 text-[15px] leading-relaxed text-muted-foreground">{intro}</p>
        )}

        <div className="mt-10 space-y-9">
          {sections.map(({ heading, body }) => (
            /* scroll-mt so an anchored heading clears the sticky header rather
               than hiding underneath it. */
            <section key={heading} id={slug(heading)} className="scroll-mt-24">
              <h2 className="text-lg font-semibold tracking-tight">{heading}</h2>
              <div className="mt-2.5 space-y-3 leading-relaxed text-muted-foreground">
                {body}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-12 border-t border-border pt-6 text-sm text-muted-foreground">
          Questions about this page?{' '}
          <Link to="/contact" className="font-medium text-foreground hover:underline">
            Get in touch
          </Link>
          .
        </p>
      </article>

        <nav
          className="mt-12 rounded-xl border border-border bg-muted/30 p-5 lg:sticky lg:top-20 lg:mt-0 lg:self-start"
          aria-label="On this page"
        >
          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            On this page
          </p>
          <ul className="mt-3 space-y-2.5">
            {sections.map(({ heading }) => (
              <li key={heading}>
                <a
                  href={`#${slug(heading)}`}
                  className="block border-l-2 border-transparent pl-3 text-sm leading-snug text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
                >
                  {heading}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  );
}
